import * as React from 'react';
import { LiveClassSession, ZoomUser } from '@magoosh/admin/live_class_sessions/types';
import { Form, Formik, FormikHelpers as FormikActions } from 'formik';
import FieldRadio from '@magoosh/lib/formik_inputs/field_radio';
import DropdownList from 'react-widgets/lib/DropdownList';
import { DefaultButton, PrimaryButton } from '@magoosh/lib/button';
import fetch from 'utilities/fetch';
import { adminPaths } from 'config/path_helpers';
import { useState } from 'react';
import CustomTooltip from '@magoosh/lib/custom_tooltip/custom_tooltip';

interface Props {
  zoomUsers: ZoomUser[];
}

interface FormValues {
  source: 'GoToWebinar' | 'Zoom';
  sessionType: 'webinar' | 'meeting';
  zoomUserId?: number;
}

const LiveClassSessionSyncForm: React.FC<Props> = (props: Props) => {
  const initialValues: FormValues = {
    source: 'Zoom',
    sessionType: 'webinar',
    zoomUserId: null
  };
  const [newSessions, setNewSessions] = useState([]);
  const [syncComplete, setSyncComplete] = useState(false);
  const [syncingUsers, setSyncingUsers] = useState(false);

  const onClickSubmit = (values: FormValues, { setSubmitting }: FormikActions<FormValues>) => {
    setSubmitting(true);
    setSyncComplete(false);
    fetch(adminPaths.api.liveClassSessionsSync(), {
      method: 'POST',
      body: JSON.stringify(values)
    })
      .then((response) => {
        setNewSessions(response.newSessions);
      })
      .catch((response) => {
        console.log(response);
        alert('Something went wrong');
      })
      .finally(() => {
        setSubmitting(false);
        setSyncComplete(true);
      });
  };

  const onClickSyncUsers = () => {
    setSyncingUsers(true);
    fetch(adminPaths.api.syncZoomUsers(), { method: 'POST' })
      .then((response) => {
        window.location.reload();
      })
      .catch((error) => alert(error))
      .finally(() => setSyncingUsers(false));
  };

  return (
    <div>
      <Formik onSubmit={onClickSubmit} initialValues={initialValues}>
        {({ values, isSubmitting, submitForm, setFieldValue }) => (
          <Form>
            <div>
              <label htmlFor="session-source">Source</label>
              <FieldRadio
                name="source"
                value="Zoom"
                id="session-source-zoom"
                checked={values.source === 'Zoom'}>
                Zoom
              </FieldRadio>
              <FieldRadio
                name="source"
                value="GoToWebinar"
                id="session-source-goto"
                checked={values.source === 'GoToWebinar'}>
                GoToWebinar
              </FieldRadio>
              {values.source === 'Zoom' && (
                <>
                  <label htmlFor="session-type">Type</label>
                  <FieldRadio
                    name="sessionType"
                    value="webinar"
                    id="session-type-webinar"
                    checked={values.sessionType === 'webinar'}>
                    Webinar
                  </FieldRadio>
                  <FieldRadio
                    name="sessionType"
                    value="meeting"
                    id="session-type-meeting"
                    checked={values.sessionType === 'meeting'}>
                    Meeting
                  </FieldRadio>
                  <label htmlFor="session-zoom-user-id">
                    Zoom User{' '}
                    <DefaultButton
                      className="u-margin-R-xxs"
                      size="xs"
                      submitting={syncingUsers}
                      onClick={onClickSyncUsers}>
                      Sync Zoom users
                    </DefaultButton>
                    <CustomTooltip
                      id={'zoom-user-tooltip'}
                      text={
                        "Every Zoom webinar belongs to the user who created it. If you don't see your Zoom account here, click 'Sync Zoom users' to fetch new zoom accounts."
                      }
                    />
                  </label>
                  <DropdownList
                    id="session-zoom-user-id"
                    name="zoomUserId"
                    data={props.zoomUsers}
                    filter="contains"
                    textField="description"
                    valueField="id"
                    dataKey="id"
                    value={values.zoomUserId}
                    onChange={(item) => setFieldValue('zoomUserId', item.id)}
                  />
                </>
              )}
              <PrimaryButton
                disabled={values.source === 'Zoom' && !values.zoomUserId}
                className="u-margin-T-m"
                submitting={isSubmitting}
                onClick={submitForm}>
                Sync
              </PrimaryButton>

              {syncComplete && (
                <>
                  {newSessions.length > 0 ? (
                    <>
                      <h5>New Sessions</h5>
                      <ul>
                        {newSessions.map((session: LiveClassSession) => (
                          <li key={session.id}>{session.name}</li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p>No New Sessions found</p>
                  )}
                </>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default LiveClassSessionSyncForm;
